import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../app/store/hooks';
import { fetchUserProfile, clearUserProfile, invalidateUserCache, resetFailureCount } from '../app/store/slices/userSlice';
import { SessionStorageService } from '../shared/services/SessionStorageService';

export const useUserProfileRedux = () => {
  const dispatch = useAppDispatch();
  const { profile: userProfile, isLoading: loading, error, consecutiveFailures } = useAppSelector((state) => state.user);
  const hasInitialized = useRef(false);
  const isFetching = useRef(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem('isLoggedIn');
    const hasToken = SessionStorageService.hasToken();
    const userEmail = sessionStorage.getItem('userEmail');
    
    console.log('useUserProfileRedux - Check conditions:', {
      hasToken,
      userEmail,
      userProfile: !!userProfile,
      hasInitialized: hasInitialized.current,
      isFetching: isFetching.current,
      consecutiveFailures,
      loading
    });
    
    // If we've hit max failures, don't try to fetch again
    if (consecutiveFailures >= 3) {
      hasInitialized.current = true;
      return;
    }
    
    // Don't mark as initialized if we don't have email yet - wait for it
    if (hasToken && !userEmail) {
      console.log('Waiting for userEmail to be set in session storage...');
      hasInitialized.current = false;
      return;
    }
    
    // Reset hasInitialized if we have a token but no profile (page refresh scenario)
    if (hasToken && userEmail && !userProfile && !loading && !isFetching.current) {
      hasInitialized.current = false;
    }
    
    // Fetch profile if:
    // - Has valid token AND user email exists
    // - Profile not already loaded
    // - Not already fetching
    // - Not already initialized (to prevent duplicate calls)
    if (hasToken && userEmail && !userProfile && !hasInitialized.current && !isFetching.current) {
      hasInitialized.current = true;
      isFetching.current = true;
      
      console.log('Fetching user profile for:', userEmail);
      
      // Set a timeout to prevent infinite loading
      fetchTimeoutRef.current = setTimeout(() => {
        if (isFetching.current) {
          console.error('Profile fetch timed out after 10 seconds');
          isFetching.current = false;
          hasInitialized.current = false; // Allow retry after timeout
        }
      }, 10000); // 10 second timeout
      
      dispatch(fetchUserProfile({ email: userEmail })).finally(() => {
        isFetching.current = false;
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
          fetchTimeoutRef.current = null;
        }
      });
    }
    
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [dispatch, userProfile, consecutiveFailures, loading]); // Added loading to dependencies

  useEffect(() => {
    const handleUserEmailUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const forceRefresh = customEvent.detail?.forceRefresh || false;
      const email = customEvent.detail?.email || sessionStorage.getItem('userEmail');
      
      dispatch(fetchUserProfile({ forceRefresh, email }));
    };

    window.addEventListener('userEmailUpdated', handleUserEmailUpdated);

    return () => {
      window.removeEventListener('userEmailUpdated', handleUserEmailUpdated);
    };
  }, [dispatch]);

  const refetchProfile = useCallback(async (forceRefresh = false, email?: string) => {
    // Reset failure count when manually retrying
    if (forceRefresh) {
      dispatch(resetFailureCount());
    }
    await dispatch(fetchUserProfile({ forceRefresh, email }));
  }, [dispatch]);

  const clearProfileCache = useCallback(() => {
    hasInitialized.current = false;
    isFetching.current = false;
    dispatch(clearUserProfile());
  }, [dispatch]);

  const invalidateCache = useCallback(() => {
    dispatch(invalidateUserCache());
  }, [dispatch]);

  return {
    userProfile,
    loading,
    error,
    refetchProfile,
    clearProfileCache,
    invalidateCache,
  };
};
